import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { supabase } from "@/lib/supabaseClient";
import type { Database } from "@/types/database.types";

type ProjectData = Database["public"]["Functions"]["get_user_projects"]["Returns"][number];

interface ProjectsContextType {
  projects: ProjectData[];
  loading: boolean;
  error: Error | null;
  refreshProjects: () => Promise<void>;
}

const ProjectsContext = createContext<ProjectsContextType | undefined>(undefined);

export function ProjectsProvider({ children }: { children: ReactNode }) {
  const [projects, setProjects] = useState<ProjectData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchProjects = useCallback(async () => {
    console.log("[ProjectsContext] fetchProjects: START");
    try {
      setError(null);
      console.log("[ProjectsContext] fetchProjects: calling supabase.rpc(get_user_projects)");
      const { data, error: fetchError } = await supabase.rpc("get_user_projects");
      console.log("[ProjectsContext] fetchProjects: RPC result", { error: fetchError, dataLength: data?.length });

      if (fetchError) {
        console.log("[ProjectsContext] fetchProjects: RPC error", fetchError);
        throw fetchError;
      }

      console.log("[ProjectsContext] fetchProjects: setting projects, count=", data?.length ?? 0);
      setProjects(data || []);
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Failed to fetch projects");
      console.log("[ProjectsContext] fetchProjects: CATCH error", error);
      setError(error);
      console.error("Error fetching projects:", error);
    } finally {
      console.log("[ProjectsContext] fetchProjects: finally, setting loading=false");
      setLoading(false);
    }
  }, []);

  const refreshProjects = useCallback(async () => {
    console.log("[ProjectsContext] refreshProjects: called, setting loading=true");
    setLoading(true);
    await fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    console.log("[ProjectsContext] useEffect: mounting, calling fetchProjects");
    fetchProjects();
  }, [fetchProjects]);

  return (
    <ProjectsContext.Provider value={{ projects, loading, error, refreshProjects }}>
      {children}
    </ProjectsContext.Provider>
  );
}

export function useProjects() {
  const context = useContext(ProjectsContext);
  if (context === undefined) {
    throw new Error("useProjects must be used within a ProjectsProvider");
  }
  return context;
}